/**
 * HeroCarousel — auto-rotating slide with prev/next + dots.
 */
import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import PromoCard from "./PromoCard";

export default function HeroCarousel({ slides, interval = 6000 }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((n) => setI((prev) => (n + slides.length) % slides.length), [slides.length]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setI((p) => (p + 1) % slides.length), interval);
    return () => clearInterval(t);
  }, [paused, slides.length, interval]);

  return (
    <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="hero-carousel">
      <div className="relative overflow-hidden rounded-2xl">
        {slides.map((s, idx) => (
          <div
            key={s.title || s.image || `slide-${idx}`}
            className={"transition-opacity duration-700 " + (idx === i ? "opacity-100" : "opacity-0 absolute inset-0 pointer-events-none")}
            data-testid={`hero-slide-${idx}`}
          >
            <PromoCard
              variant="hero"
              image={s.image}
              title={s.title}
              subtitle={s.subtitle}
              cta={s.cta}
              onCta={s.onCta}
              testid={`hero-promo-${idx}`}
            />
          </div>
        ))}
      </div>

      <button
        onClick={() => go(i - 1)}
        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-base/60 backdrop-blur border border-line text-ink-2 hover:text-ink hover:bg-base flex items-center justify-center"
        aria-label="Previous"
        data-testid="hero-prev"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={() => go(i + 1)}
        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-base/60 backdrop-blur border border-line text-ink-2 hover:text-ink hover:bg-base flex items-center justify-center"
        aria-label="Next"
        data-testid="hero-next"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {slides.map((s, idx) => (
          <button
            key={s.title ? `dot-${s.title}` : `dot-${idx}`}
            onClick={() => setI(idx)}
            className={
              "h-1.5 rounded-full transition-all shadow-card " +
              (idx === i ? "w-8 bg-pink" : "w-2 bg-white/40 hover:bg-white/70")
            }
            aria-label={`Slide ${idx + 1}`}
            data-testid={`hero-dot-${idx}`}
          />
        ))}
      </div>
    </div>
  );
}
